import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from "@angular/core";
import { confirm } from "devextreme/ui/dialog";
import { ProductionService } from "src/app/modules/production/_api/services/production.service";
import { ResourcesService } from "src/app/modules/production/_api/services/resources.service";
import { PlanningService } from "../../../modules/production/_api/services/planning.service";
import { AmsNotifierService } from "../../services/ams-notifier.service";
import { LanguageService } from "../../services/language.service";
import { UserService } from "../../services/user.service";
import { GanttTask } from "../gantt/task.model";
import { SchedulerEvent } from "../scheduler/event.model";
import { SplitJobViewModel } from "./split-job.viewmodel";

@Component({
  selector: "app-scheduler-popup",
  templateUrl: "./scheduler-popup.component.html",
  styleUrls: ["./scheduler-popup.component.css"]
})
export class SchedulerPopupComponent implements OnChanges {
  @Input() isPopupVisible: boolean;
  @Input() showTitle = true;
  @Input() event: SchedulerEvent;
  @Input() connectedEvents: SchedulerEvent[];
  @Input() resources: any[];

  @Output() actionExecutedThatRequiresReload = new EventEmitter<boolean>();
  @Output() closeClicked = new EventEmitter<SchedulerEvent>();

  tasks: GanttTask[];
  isLoadingDependencies = false;
  isBusy = false;
  isEmployee = true;
  connectableResources: any[];

  isSplitJobPopupVisible = false;
  resourcesWithinWorkgroup: any[];
  resourceIdsToSplitJobBetween: number[] = [];

  readonly successResponse = "success";

  get splitButtonLabel(): string {
    return `${this.languageService.LNG("Label.Move")} / ${this.languageService.LNG("Label.Split")}`;
  }

  constructor(
    public languageService: LanguageService,
    private planningService: PlanningService,
    private productionService: ProductionService,
    private notifier: AmsNotifierService,
    private userService: UserService,
    private resourcesService: ResourcesService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (!this.event || !changes.event || changes.event.previousValue === changes.event.currentValue) { return; }
    this.getDependencies();
    this.checkIfResourceIsEmployee();
    this.getResourcesWithinWorkgroup();
    if (!this.isEmployee) { this.getConnectableResources(); }
  }

  private async getResourcesWithinWorkgroup() {
    this.resourcesWithinWorkgroup = null;
    const resource = await this.resourcesService.get(this.event.data.ResourceId).toPromise();
    if (!resource) return;
    this.resourcesWithinWorkgroup = this.resources.filter(r => r.WorkgroupId == resource.WorkgroupId);
  }

  private async getDependencies() {
    this.tasks = null;
    this.isLoadingDependencies = true;
    const operations = await this.planningService.getDependentOperations(this.event.data.OperationId).toPromise();
    this.convertOperationsToTasks(operations);

    // Funny thing: Gantt diagram is not shown correctly in production build unless there is a timeout between data insertion and repaint
    setTimeout(() => this.isLoadingDependencies = false, 1000);
  }

  private checkIfResourceIsEmployee() {
    this.isEmployee = this.resources.find(r => r.ResourceId === this.event.data.ResourceId && r.IsEmployee);
  }

  private async getConnectableResources() {
    this.connectableResources = await this.productionService.getConnectableEmployeeResourcesByJob(this.event.data.OperationResourceId).toPromise();
  }

  private convertOperationsToTasks(operations: any) {
    const tasks = [];
    operations.forEach((o: any) => {
      const operationId = `o_${o.OperationId}`;
      tasks.push({ id: operationId, parentId: 0, title: o.Name, start: o.PlannedStart, end: o.PlannedStop });
      o.OperationResources.forEach((or: any) => {
        tasks.push({ id: or.OperationResourceId, parentId: operationId, title: or.ResourceName, start: or.PlannedStart, end: or.PlannedStop });
      });
    });

    this.tasks = tasks;
  }

  async completeEvent() {
    const confirmed = await confirm(`${this.languageService.LNG("Label.Button.Complete")}?`, this.languageService.LNG("Label.Confirm"));
    if (!confirmed) { return; }

    const resourceActivityId = this.userService.account.SystemSettings._11;
    if (!resourceActivityId) {
      this.notifier.errorWithTimeout("System parameter 11 is not assigned (Planning_Finish_ResourceActivityId)");
      return;
    }

    this.isBusy = true;
    const response = await this.planningService.completeJob(this.event, resourceActivityId).toPromise();
    this.isBusy = false;

    if (!response.IsSuccess) {
      this.notifier.errorWithTimeout(response.ErrorMessage);
      return;
    }

    this.requestReload();
  }

  async saveEvent() {
    this.isBusy = true;
    const response = await this.planningService.updateJob(this.event).toPromise();
    this.isBusy = false;

    if (response !== this.successResponse) {
      this.notifier.errorWithTimeout(response);
      return;
    }

    this.requestReload();
  }

  async deleteEvent() {
    const confirmed = await confirm(this.languageService.LNG("Label.Confirm.Delete"), this.languageService.LNG("Label.Confirm"));
    if (!confirmed) { return; }

    this.isBusy = true;
    const response = await this.planningService.deleteJob(this.event).toPromise();
    this.isBusy = false;

    if (response !== this.successResponse) {
      this.notifier.errorWithTimeout(response);
      return;
    }

    this.requestReload();
  }

  async addConnectedJob(e: any) {
    const event = e.data;
    const newData = Object.assign({}, this.event.data);
    const newEvent = Object.assign({}, this.event);

    newData.ResourceId = event.data.ResourceId;
    newData.ParentResourceId = this.event.data.ResourceId;
    newData.parentOperationResourceId = this.event.data.OperationResourceId;

    newEvent.data = newData;
    newEvent.startDate = event.startDate;
    newEvent.endDate = event.endDate;
    newEvent.locked = false;

    this.isBusy = true;
    const response = await this.planningService.planJobByResource(newData.ResourceId, newEvent).toPromise();
    this.isBusy = false;

    if (!response.startsWith("success")) {
      this.notifier.errorWithTimeout(response);
      return;
    }

    this.requestReload(false);
  }

  async updateConnectedJob(e: any) {
    const data = { ... e.oldData.data, ... e.newData.data };
    const event = { ... e.oldData, ... e.newData };

    event.data = data;

    console.log(event);

    this.isBusy = true;
    const response = await this.planningService.updateJob(event).toPromise();
    this.isBusy = false;

    if (response !== "success") {
      this.notifier.errorWithTimeout(response);
      return;
    }

    // e.component.cancelEditData();
    this.requestReload(true);
  }

  async removeConnectedJob(e: any) {
    e.cancel = true;
    const event = e.data;

    this.isBusy = true;
    const response = await this.planningService.deleteJob(event).toPromise();
    this.isBusy = false;

    if (response !== "success") {
      this.notifier.errorWithTimeout(response);
      return;
    }

    this.connectedEvents = this.connectedEvents.filter(e => e.data.OperationResourceId !== event.data.OperationResourceId);
    this.requestReload(true);
  }

  private requestReload(keepOpen = false) {
    this.actionExecutedThatRequiresReload.emit(keepOpen);
  }

  onSplitJobResourceSelectionChanged(resources: any) {
    const columns = [];
    resources.forEach((r: any) => { columns.push(r.ResourceId); });
    this.resourceIdsToSplitJobBetween = columns;
  }

  async splitJobsBetweenResources() {
    const viewModel: SplitJobViewModel = {
      OperationResourceId: this.event.data.OperationResourceId,
      ResourceIds: this.resourceIdsToSplitJobBetween
    };

    this.isBusy = true;
    const response = await this.planningService.splitJob(viewModel).toPromise();
    this.isBusy = false;

    if (response !== this.successResponse) {
      this.notifier.errorWithTimeout(response);
      return;
    }

    this.isSplitJobPopupVisible = false;
    this.requestReload();
  }

  onEmployeeResourceSelectionChanged(selectedRowKeys: any, cellInfo: any, dropDownBoxComponent: any) {
    cellInfo.setValue(selectedRowKeys[0]);
    if (selectedRowKeys.length > 0) {
      dropDownBoxComponent.close();
    }
  }
}
